import { useEffect, useMemo, useState } from "react";
import { fetchWisdomODBMDashboard } from "services/wisdom/wisdomData";
import { TABS, Kpi, Ranking, TrendTab, bandColor, fmt } from "components/dashboard/ODBMDashboard";

const tab = TABS.find((t) => t.key === "STA");

// Same rules as the TG (انتظام التدريب) benchmark: senior students, these statuses count as present, UTC dates.
const PRESENT_STATUSES = ["attend", "late", "left with parent"];
const pct = (b) => (b && b.total ? Math.round((b.present / b.total) * 100) : null);

function buildAttendanceStats(dashboard, year, month) {
  const records = dashboard?.seniorStudentsAttendance;
  if (!records) return null;

  const y = Number(year);
  const m = Number(month) || 0;
  const today = new Date();
  const isCurrentMonth = today.getUTCFullYear() === y && today.getUTCMonth() === m;
  const lastDay = isCurrentMonth ? today.getUTCDate() : new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const days = Array.from({ length: Math.max(lastDay, 1) }, (_, i) => i + 1);

  const total = { present: 0, total: 0 };
  const byDay = {};
  const bySpec = {};
  records.forEach((r) => {
    const d = new Date(r.createdAt);
    if (d.getUTCFullYear() !== y || d.getUTCMonth() !== m) return;
    const present = PRESENT_STATUSES.includes(r.status);
    const day = d.getUTCDate();
    const specName = r.student?.specialization?.name || "غير محدد";

    [total, (byDay[day] ??= { present: 0, total: 0 }), (bySpec[specName] ??= { present: 0, total: 0 })].forEach((b) => {
      b.total += 1;
      if (present) b.present += 1;
    });
  });

  return {
    days,
    overall: pct(total),
    daily: days.map((day) => pct(byDay[day])),
    units: Object.entries(bySpec)
      .map(([name, b]) => [name, pct(b) ?? 0])
      .sort((a, b) => b[1] - a[1]),
  };
}

const StudentAttendanceDashboard = ({ orgId, year, month, onClose }) => {
  const [fetching, setFetching] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setFetching(true);
        const response = await fetchWisdomODBMDashboard(orgId, year, month);
        setDashboard(response || null);
      } catch (err) {
        console.error("error:", err);
      } finally {
        setFetching(false);
      }
    };

    load();
  }, [orgId, year, month]);

  const stats = useMemo(() => buildAttendanceStats(dashboard, year, month), [dashboard, year, month]);

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-[rgba(5,10,20,0.62)] p-4"
      style={{ fontFamily: '"Segoe UI", Tahoma, "Noto Sans Arabic", Arial, sans-serif' }}
    >
      <div
        dir="rtl"
        className="relative flex w-full max-w-[660px] max-h-[min(92vh,760px)] flex-col overflow-hidden rounded-2xl border border-[#33507E] bg-[#0F1A2B] text-[#EAF1FA] shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
        role="dialog"
        aria-modal="true"
        aria-label="لوحة تحليل حضور الطلاب"
      >
        <div className="flex flex-none items-center gap-2 border-b border-[#26385A] bg-[#16233A] px-3.5 py-2.5">
          <div>
            <h1 className="m-0 text-sm font-semibold leading-snug">
              {tab.label}
              <span className="ms-1 inline-block rounded-full border border-[rgba(46,147,204,0.32)] bg-[rgba(46,147,204,0.14)] px-2 py-0.5 align-[1px] text-[9.5px] text-[#8FCBEC]">
                بيانات عملية
              </span>
            </h1>
            <div className="mt-0.5 text-[10.5px] text-[#6C82A3]">
              {year ? `${Number(month || 0) + 1} / ${year}` : "كل الفترات"}
            </div>
          </div>
          <div className="ms-auto flex flex-none gap-1.5">
            <button
              type="button"
              aria-label="إغلاق"
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-lg border border-[#26385A] text-sm text-[#6C82A3] transition-colors hover:bg-[#1C2C47] hover:text-[#EAF1FA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E93CC] focus-visible:ring-offset-1"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5 pt-2.5 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-[#33507E]">
          {fetching && <div className="px-2 py-6 text-center text-xs text-[#6C82A3]">جارِ تحميل البيانات...</div>}

          {!fetching && !stats && (
            <div className="px-2 py-6 text-center text-xs text-[#6C82A3]">لا توجد بيانات كافية لعرض التحليل.</div>
          )}

          {!fetching && stats && (
            <>
              <div className="mb-2.5 grid grid-cols-2 gap-1.5">
                <Kpi label="نسبة الشهر" value={fmt(stats.overall)} color={bandColor(stats.overall, tab.tgt)} />
                <Kpi label="المستهدف" value={fmt(tab.tgt)} color="#9DB2CE" />
              </div>
              <TrendTab tab={tab} days={stats.days} daily={stats.daily} />
              <Ranking tab={tab} units={stats.units} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAttendanceDashboard;
